import { queryOptions } from "@tanstack/react-query";
import { format } from "date-fns";
import fetcher from "@/core/lib/fetcher";
import { getStartOfMonthToToday } from "@/core/lib/utils";
import type { APIResponse } from "@/core/types/utils";
import type {
	CommissionDetailRow,
	CommissionSummaryRow,
	CommissionsPage,
} from "./types";

/** Search params every link into the report must carry. Defaults to
 *  month-to-date, the range payouts are usually reviewed by. */
export const defaultCommissionsSearch = () => {
	const { from, to } = getStartOfMonthToToday();
	return {
		dateFrom: format(from, "yyyy-MM-dd"),
		dateTo: format(to, "yyyy-MM-dd"),
		tab: "summary" as const,
		page: 1,
		pageSize: 10,
		propertyId: "",
		bookingId: "",
		purchaseId: "",
	};
};

export type CommissionsReportQuery = {
	token: string;
	groupId: string;
	dateFrom: string;
	dateTo: string;
	page: number;
	limit: number;
	/** Only the detail report carries these; the summary aggregates by property. */
	propertyId?: string;
	bookingId?: string;
	purchaseId?: string;
};

const buildParams = ({
	groupId,
	dateFrom,
	dateTo,
	propertyId,
	bookingId,
	purchaseId,
}: Omit<CommissionsReportQuery, "token" | "page" | "limit">) => {
	const sp = new URLSearchParams({ groupId, dateFrom, dateTo });
	if (propertyId) sp.set("propertyId", propertyId);
	if (bookingId) sp.set("bookingId", bookingId);
	if (purchaseId) sp.set("purchaseId", purchaseId);
	return sp;
};

const buildUrl = (report: "summary" | "detail", q: CommissionsReportQuery) => {
	const sp = buildParams(q);
	sp.set("page", String(q.page));
	sp.set("limit", String(q.limit));
	return `${import.meta.env.VITE_API_BOOKING}payouts/reports/commissions/${report}?${sp.toString()}`;
};

const emptyPage = <T>(limit: number): CommissionsPage<T> => ({
	items: [],
	meta: { page: 1, limit, total: 0, totalPages: 0, hasNextPage: false },
});

export const commissionsSummaryQueryOptions = (q: CommissionsReportQuery) =>
	queryOptions({
		queryKey: ["commissions-summary", q],
		queryFn: () =>
			fetcher
				.auth(`Bearer ${q.token}`)
				.get(buildUrl("summary", q))
				.notFound(() => ({ data: emptyPage<CommissionSummaryRow>(q.limit) }))
				.json<APIResponse<CommissionsPage<CommissionSummaryRow>>>(),
	});

export const commissionsDetailQueryOptions = (q: CommissionsReportQuery) =>
	queryOptions({
		queryKey: ["commissions-detail", q],
		queryFn: () =>
			fetcher
				.auth(`Bearer ${q.token}`)
				.get(buildUrl("detail", q))
				.notFound(() => ({ data: emptyPage<CommissionDetailRow>(q.limit) }))
				.json<APIResponse<CommissionsPage<CommissionDetailRow>>>(),
	});

/**
 * Downloads the whole range as a single workbook — one sheet per report.
 *
 * The endpoint caps how many rows it will render; when the cap is hit the
 * response carries `X-Export-Truncated: true` and `truncated` is surfaced to
 * the caller so the UI can prompt the user to narrow the range.
 */
export const downloadCommissionsXlsx = (
	q: Omit<CommissionsReportQuery, "page" | "limit">,
	name: string,
): Promise<{ blob: Blob; truncated: boolean }> => {
	const sp = buildParams(q);
	if (name) sp.set("name", name);
	return fetcher
		.auth(`Bearer ${q.token}`)
		.get(
			`${import.meta.env.VITE_API_BOOKING}payouts/reports/commissions/xlsx?${sp.toString()}`,
		)
		.res(async (res) => ({
			blob: await res.blob(),
			truncated: res.headers.get("X-Export-Truncated") === "true",
		}));
};
